/**
 * useCurrentUser — identity bootstrap for the SPA.
 *
 * Reads /auth/me from tax-account on mount, caches for 5min. The
 * dashboard header relies on this to render the avatar + active-org
 * dropdown; protected routes rely on it to detect a stale token.
 *
 * On 401 the response interceptor in lib/api.ts already wipes the
 * in-memory token and bounces to /login — so this hook just needs
 * to surface loading/error to the consumer.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi, hasValidAccessToken, type MeResponse } from '@/lib/api'

const QUERY_KEY = ['auth', 'me'] as const

export function useCurrentUser() {
  return useQuery<MeResponse>({
    queryKey: QUERY_KEY,
    queryFn: () => authApi.me(),
    enabled: hasValidAccessToken(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, err: unknown) => {
      // Don't retry 401 — the interceptor will redirect to /login.
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 401) return false
      return failureCount < 1
    },
  })
}

/** Invalidate the cached profile (call after switching orgs, etc). */
export function useInvalidateCurrentUser() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: QUERY_KEY })
}
